/**
 * DCS AI-assistant widget loader — the cms head-inject seam for Phase 3 of the
 * Managed Site AI Agents plan (.docs/archive/plans/managed-site-ai-agents/plan.md).
 *
 * This rides the same always-on `dcsContentPlugin.transformIndexHtml` seam that
 * ships the motion tokens: every DCS site already wires `dcsContentPlugin`, so a
 * cms version bump delivers this loader to the whole fleet with ZERO per-site
 * source edits, and the portal per-site toggle turns the widget on/off at
 * runtime with no rebuild.
 *
 * What gets injected is a tiny (<2 KB) inline `<script>` — NOT the widget. On
 * idle it fetches the public agent config for this site; only if the agent is
 * enabled (HTTP 200) does it publish `window.__DCS_AGENT_BOOTSTRAP__` and inject
 * the real widget bundle from the CDN. The loader is **fail-silent**: a disabled
 * agent (404), a dead backend, a blocked request, or a malformed response all
 * result in no mount and no console error, so the assistant can never break a
 * customer page.
 *
 * Build-only: the loader is injected into the production build output (the fleet
 * delivery), not into `vite dev`. In local dev the config fetch would hit the
 * prod API from a non-whitelisted `localhost` origin and log an uncatchable CORS
 * error for every site developer — so, unlike the constant motion-token block,
 * this network-touching loader stays out of the dev server.
 */

import fs from 'node:fs'
import path from 'node:path'

import yaml from 'js-yaml'
import type { HtmlTagDescriptor } from 'vite'

/** DCS API origin the loader calls (matches the cms runtime composables). */
export const DEFAULT_AGENT_API_BASE_URL = 'https://portal.duffcloudservices.com'

/**
 * Versioned CDN URL of the widget bundle. Both `cdn.` (branding CDN, CorsAllowAll)
 * and the already-live `files.` route serve the same `sgdcs` blob origin with an
 * identical `/agent-widget/v1/...` layout, so this is a plain classic-script
 * include that needs no CORS on the bundle host. Defaults to `files.` because the
 * `cdn.` Front Door route is still provisioning-gated (enableCdnDomain=false) —
 * flip the default only after that domain is live, or pass cdnScriptUrl.
 */
export const DEFAULT_AGENT_WIDGET_SCRIPT_URL =
  'https://files.duffcloudservices.com/agent-widget/v1/dcs-agent-widget.js'

export interface DcsAgentWidgetLoaderOptions {
  /** Owning site slug — baked into the loader so it can build the config URL. */
  siteSlug: string
  /** DCS API base (default: the shared portal origin). */
  apiBaseUrl?: string
  /** Full URL of the CDN widget bundle. */
  cdnScriptUrl?: string
}

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '')

/**
 * JSON-encodes a value for safe embedding inside an inline `<script>` body.
 * `JSON.stringify` alone stops JS string-literal breakout but NOT the HTML
 * parser: a raw `</script>` (or a U+2028/U+2029 line separator, which is a valid
 * JS line terminator) inside the string would still terminate the element. So we
 * additionally escape `<`, `>`, and the two separators to their `\uXXXX` forms —
 * defense-in-depth even though today every embedded value is build-time DCS
 * provisioning identity (DNS-label slug + platform URLs).
 */
const encodeForInlineScript = (value: string): string =>
  JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')

/**
 * Builds the inline loader script body. Slug + URLs are HTML-safe-encoded (see
 * encodeForInlineScript) so no value can break out of the string literal OR the
 * `<script>` element. Kept intentionally compact to stay under the ~2 KB perf
 * budget (G4).
 */
export function buildAgentWidgetLoaderScript(options: DcsAgentWidgetLoaderOptions): string {
  const slug = encodeForInlineScript(options.siteSlug)
  const api = encodeForInlineScript(trimTrailingSlash(options.apiBaseUrl || DEFAULT_AGENT_API_BASE_URL))
  const cdn = encodeForInlineScript(options.cdnScriptUrl || DEFAULT_AGENT_WIDGET_SCRIPT_URL)

  return (
    '(function(){try{' +
    'if(window.__DCS_AGENT_LOADER__)return;window.__DCS_AGENT_LOADER__=1;' +
    // Suppression parity with PreviewRibbon: skip the editor iframe (self!==top,
    // cross-origin access throws → treated as framed) and the ?dcs-hide-widget
    // screenshot/perf suppressor.
    'try{if(window.self!==window.top)return;}catch(e){return;}' +
    'if(/[?&]dcs-hide-widget(?:[=&]|$)/.test(location.search))return;' +
    'var S=' + slug + ',A=' + api + ',C=' + cdn + ';' +
    'var go=function(){try{' +
    // C-180: slug-free bootstrap probe. The loader runs on the customer's own
    // page, so a SAME-ORIGIN /api/v1/agent/config carries the customer Host and
    // SiteContextMiddleware resolves the site server-side — no slug in the path.
    // (Baking the slug into A+"/api/v1/sites/"+S+... hit the shared portal Host,
    // which cannot resolve a customer site.) A (apiBaseUrl) and S (slug) are still
    // handed to the widget bundle below for the chat/contact calls, which remain
    // slug-based against the legacy routes until they are migrated too.
    //
    // FALLBACK (2026-07-09 live finding): not every customer apex proxies
    // /api/v1/* to an API — kept's apex serves it from the STATIC origin (200
    // text/html SPA fallback), so the same-origin probe gets HTML and json()
    // rejects. When the slug-free probe yields nothing, retry the legacy
    // cross-origin slug route so the widget keeps mounting on proxy-less sites.
    //
    // C-290 (2026-07-25, probe matrix
    // .docs/plans/dynamic-site-resolution/evidence/agent-config-cors-matrix-2026-07-25.json):
    // the two arms were ALREADY sequential — the defect was that p() collapsed
    // "no API on this route" and "the API answered, there is no agent" into the
    // same null, so a site whose API authoritatively 404s fired a SECOND
    // guaranteed-404 request and logged a second uncatchable browser console
    // error on every page load. p() now distinguishes them:
    //   null -> no API reached (network error, or a non-JSON body = SPA
    //           fallback) => the legacy arm is worth trying
    //   0    -> a JSON API answered but the agent is off/absent => STOP, the
    //           legacy arm can only 404 too
    // 0 is falsy, so the mount gate below is unchanged.
    'var p=function(u){return fetch(u,{headers:{Accept:"application/json"}})' +
    // NB: no `<` / `>` anywhere in this body — the inline script must stay
    // angle-bracket-free so nothing can terminate the <script> element
    // (asserted by the HTML-hostile-slug test), hence indexOf(...)===-1.
    '.then(function(r){if(!r||(r.headers.get("content-type")||"").indexOf("json")===-1)return null;' +
    'return r.ok?r.json()["catch"](function(){return null;}):0;})' +
    '["catch"](function(){return null;});};' +
    'p("/api/v1/agent/config")' +
    '.then(function(c){return c===null?p(A+"/api/v1/sites/"+encodeURIComponent(S)+"/agent/config"):c;})' +
    '.then(function(c){if(!c||!c.enabled)return;' +
    'window.__DCS_AGENT_BOOTSTRAP__={siteSlug:S,apiBaseUrl:A,config:c};' +
    'var s=document.createElement("script");s.src=C;s.async=true;s.setAttribute("data-dcs-agent-slug",S);' +
    '(document.head||document.documentElement).appendChild(s);})' +
    '.catch(function(){});' +
    '}catch(e){}};' +
    'if("requestIdleCallback"in window){requestIdleCallback(go,{timeout:4000});}else{setTimeout(go,1200);}' +
    '}catch(e){}})();'
  )
}

const SITE_YAML_CANDIDATES = ['.dcs/site.yaml', '../.dcs/site.yaml']

/**
 * Reads `site_slug` from the site's `.dcs/site.yaml` (the same identity file the
 * announcements/provisioning path uses). Returns null when no site.yaml exists
 * or it carries no slug — the caller then injects nothing (silence for non-DCS
 * builds).
 */
export function readSiteSlug(projectRoot: string): string | null {
  const candidates = [
    ...SITE_YAML_CANDIDATES.map((rel) => path.resolve(projectRoot, rel)),
    path.resolve(process.cwd(), '.dcs/site.yaml'),
  ]
  for (const candidate of candidates) {
    try {
      if (!fs.existsSync(candidate)) continue
      const parsed = yaml.load(fs.readFileSync(candidate, 'utf8')) as { site_slug?: unknown } | null
      const slug = parsed && typeof parsed.site_slug === 'string' ? parsed.site_slug.trim() : ''
      if (slug) return slug
    } catch {
      // Unreadable/unparseable — try the next candidate, else return null.
    }
  }
  return null
}

export interface DcsAgentWidgetTagOptions {
  /** Vite project root used to resolve the site slug. */
  projectRoot: string
  /** Override the DCS API base (default: process.env.VITE_API_BASE_URL, else the shared origin). */
  apiBaseUrl?: string
  /** Override the CDN bundle URL. */
  cdnScriptUrl?: string
}

/**
 * The `<head>` tag descriptor for the loader, or `null` when this build is not a
 * DCS-managed site (no resolvable slug) — in which case nothing is injected.
 */
export function dcsAgentWidgetLoaderTag(options: DcsAgentWidgetTagOptions): HtmlTagDescriptor | null {
  const siteSlug = readSiteSlug(options.projectRoot)
  if (!siteSlug) return null

  return {
    tag: 'script',
    attrs: { 'data-dcs-agent-loader': '' },
    children: buildAgentWidgetLoaderScript({
      siteSlug,
      apiBaseUrl: options.apiBaseUrl,
      cdnScriptUrl: options.cdnScriptUrl,
    }),
    injectTo: 'head',
  }
}
